import React, { Component } from 'react'
import styles from './styles.css'
import {
  defineMessages,
  FormattedMessage
} from 'react-intl'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import cn from 'classnames'
import {
  VOLUME_TEST_TIMEOUT,
  TIMEOUT_BEFORE_REDIRECT,
  MIN_BANDWIDTH_MB,
  SUCCESS,
  FAIL
} from 'constants/equpmentTests'
import DefaultMicrophone from 'images/default-microphone.png'
import DefaultBandwidth from 'images/default-bandwidth.png'
import TestingCamera from 'images/testing-camera.png'
import TestingMicrophone from 'images/testing-microphone.png'
import TestingBandwidth from 'images/testing-bandwidth.png'
import SuccessCamera from 'images/success-camera.png'
import SuccessMicrophone from 'images/success-microphone.png'
import SuccessBandwidth from 'images/success-bandwidth.png'
import FailCamera from 'images/fail-camera.png'
import FailMicrophone from 'images/fail-microphone.png'
import FailBandwidth from 'images/fail-bandwidth.png'
import Modal from 'components/Modal'
import Button from 'components/Button'
import {
  CAMERA_ERROR_MODAL,
  AUDIO_ERROR_MODAL,
  SPEED_ERROR_MODAL
} from 'constants/modals'
import SpeedTestHelper from 'utils/SpeedTestHelper'
import {
  testAudioInputDevice,
  AudioInputTest,
  testVideoInputDevice,
  VideoInputTest
} from '@twilio/rtc-diagnostics'
import {AUDIO_LEVEL_THRESHOLD} from '../../constants/equpmentTests'

const messages = defineMessages({
  introduction: 'We\'re going to test your video and audio connection for you',
  successMessage: 'Your video, audio, and internet connection are working properly.',
  failureMessage: 'There are issues with your equipment/internet connection.',
  camera: 'Camera',
  videoInstruction: 'Do you see yourself?',
  yes: 'Yes',
  no: 'No',
  microphone: 'Microphone',
  speed: 'Speed',
  speakerInstruction: 'Could you hear youself?',
  audioInstruction: 'Speak into your mic to record a message. ',
  audioInstruction2: 'Your message will be then played back to you.',
  bandwidth: 'Your current bandwidth is',
  latency: 'average latency',
  runAgain: 'Run tests again.',
  backToScheduling: 'Back to Scheduling',
  cameraErrorModalTitle: 'Camera Error',
  cameraErrorModalTips: 'Make sure your webcam is plugged in or not being used by another program.',
  audioErrorModalTitle: 'Audio Error',
  audioErrorModalTips: 'Check that your mic and/or headphones are plugged into the correct port and that the neither is muted.',
  connectionErrorModalTitle: 'Connection Error',
  connectionErrorModalTips: 'You can only video visit if your speedometer is green. You need a high-speed internet connection for a video visit.',
  errorModalContinueOrSkip: 'Click Continue to test again or skip to the next test.',
  skip: 'Skip',
  continue: 'Continue'
})

class EquipmentTests extends Component{

  constructor(props) {
    super(props)
    this.state = {
      showIntroduction: false,
      videoTestIsActive: false,
      showVideoTestInstruction: false,
      showVideoTestResult: false,
      showSpeakerTestResult: false,
      cameraStatusIcon: null,
      microphoneStatusIcon: null,
      bandwidthStatusIcon: null,
      videoTest: null
    }
    this.startVideoTest = this.startVideoTest.bind(this)
    this.completeVideoTest = this.completeVideoTest.bind(this)
    this.rerun = this.rerun.bind(this)
    this.videoEl = React.createRef()
  }

  componentDidMount() {
    this.setState({
      showIntroduction: true,
      videoTestIsActive: true,
      showVideoTestInstruction: true,
      showVideoTestResult: true,
      cameraStatusIcon: TestingCamera,
      microphoneStatusIcon: DefaultMicrophone,
      bandwidthStatusIcon: DefaultBandwidth
    }, () => this.startVideoTest())
  }

  startVideoTest(stream) {
    const videoInputDeviceTest = testVideoInputDevice({ element: this.videoEl.current })
    videoInputDeviceTest.on(VideoInputTest.Events.Error, (error) => {
      console.error(error)
      this.props.openModal(CAMERA_ERROR_MODAL)
    })
    this.setState({
      videoTest: videoInputDeviceTest
    })
  }

  completeVideoTest(result) {
    this.state.videoTest.stop()
    this.setState({
      showVideoTestResult: false,
      showVideoTestInstruction: false,
      videoTestIsActive: false,
      cameraStatusIcon: result === SUCCESS ? SuccessCamera : FailCamera,
      videoTestResult: result === SUCCESS
    }, () => this.startAudioTest())
  }

  startAudioTest() {
    const audioInputDeviceTest = testAudioInputDevice({ enableRecording: true, duration: VOLUME_TEST_TIMEOUT })
    const getAudioLevelPercentage = (level) => {
      return Math.trunc((level * 100) / AUDIO_LEVEL_THRESHOLD) // 0 to 100
    }

    audioInputDeviceTest.on(AudioInputTest.Events.Volume, (volume) => {
      this.setState({ volume: getAudioLevelPercentage(volume) })
    })

    audioInputDeviceTest.on(AudioInputTest.Events.Error, (error) => {
      console.error(error)
      this.props.openModal(AUDIO_ERROR_MODAL)
    })

    audioInputDeviceTest.on(AudioInputTest.Events.End, (report) => {
      this.setState({ volume: getAudioLevelPercentage(0) })
      setTimeout(() => this.showAudioTestResult(report.recordingUrl), 500)
    })

    this.setState({
      microphoneStatusIcon: TestingMicrophone,
      audioTestIsActive: true
    })
  }

  showAudioTestResult(playbackURI) {
    const audioContainer = document.getElementById('audio-stream'),
      audioEl = document.createElement('audio')

    audioEl.src = playbackURI
    audioEl.controls = true
    audioEl.onended = () => {
      this.setState({
        showSpeakerTestResult: true,
        showSpeakerTestInstruction: true
      })
    }

    audioContainer.append(audioEl)
    this.setState({ audioAttached: true })
  }

  completeAudioTest(result) {
    this.setState({
      audioTestIsActive: false,
      showSpeakerTestResult: false,
      showSpeakerTestInstruction: false,
      microphoneStatusIcon: result === SUCCESS ? SuccessMicrophone : FailMicrophone,
      audioTestResult: result === SUCCESS
    })
    this.startSpeedTest()
  }

  startSpeedTest() {
    this.setState({
      bandwidthStatusIcon: TestingBandwidth,
      speedTestIsActive: true
    })
    this.startConnectionDetection()
  }

  startConnectionDetection() {
    new SpeedTestHelper().run((res) => {
      if(res.latencyInMS !== null && res.bandwidthInMB !== null) {
        this.setState({
          latency: res.latencyInMS,
          bandwidth: res.bandwidthInMB,
          showSpeedResult: true
        })

        if(res.bandwidthInMB >= MIN_BANDWIDTH_MB) {
          this.completeSpeedTest(SUCCESS)
        } else {
          this.props.openModal(SPEED_ERROR_MODAL)
        }
      }
    })
  }

  completeSpeedTest(result) {
    this.setState({
      speedTestIsActive: false,
      bandwidthStatusIcon: result === SUCCESS ? SuccessBandwidth : FailBandwidth,
      showIntroduction: false,
      speedTestResult: result === SUCCESS
    })
    this.processTestResults()
  }

  processTestResults() {
    if(this.state.videoTestResult && this.state.audioTestResult && this.state.speedTestResult) {
      this.setState({ showSuccessMessage: true })
      setTimeout(() => {
        if (this.props.visitDetailsUrl) {
          window.location.href = this.props.visitDetailsUrl
        }
      }, TIMEOUT_BEFORE_REDIRECT)
    } else {
      this.setState({
        showFailureMessage: true,
        showRunAgainMessage: true
      })
    }
  }

  rerun() {
    const search =  window.location.hash.split('?')[1]
    this.props.history.push('/tests-rerun' + (search ? `?${search}` : ''))
  }

  render() {
    const {
      showIntroduction,
      showSuccessMessage,
      showFailureMessage,
      videoTestIsActive,
      audioTestIsActive,
      speedTestIsActive,
      showRunAgainMessage,
      showVideoTestInstruction,
      showVideoTestResult,
      showSpeakerTestInstruction,
      showSpeakerTestResult,
      showSpeedResult,
      bandwidth,
      latency,
      volume,
      cameraStatusIcon,
      microphoneStatusIcon,
      bandwidthStatusIcon,
      audioAttached
    } = this.state

    const {
      showCameraErrorModal,
      showAudioErrorModal,
      showSpeedErrorModal,
      openModal,
      closeModal,
      visitDetailsUrl
    } = this.props

    return (
      <div styleName='testsPage'>
        <Modal
          title={(
            <div className={styles.modalHeader}>
              <img src={FailCamera} alt="Camera error icon" />
              <FormattedMessage {...messages.cameraErrorModalTitle} />
            </div>
          )}
          show={showCameraErrorModal}
          footer={(
            <div className={styles.modalFooter}>
              <Button
                type='cancel'
                onClick={() => {
                  this.completeVideoTest(FAIL)
                  closeModal(CAMERA_ERROR_MODAL)
                }}
              >
                <FormattedMessage {...messages.skip} />
              </Button>
              <Button
                type='ok'
                onClick={() => {
                  closeModal(CAMERA_ERROR_MODAL)
                }}
              >
                <FormattedMessage {...messages.continue} />
              </Button>
            </div>
          )}
        >
          <ul>
            <li>
              <FormattedMessage {...messages.cameraErrorModalTips} />
            </li>
            <li>
              <FormattedMessage {...messages.errorModalContinueOrSkip} />
            </li>
          </ul>
        </Modal>

        <Modal
          title={(
            <div className={styles.modalHeader}>
              <img src={FailMicrophone} alt="Microphone error icon" />
              <FormattedMessage {...messages.audioErrorModalTitle} />
            </div>
          )}
          show={showAudioErrorModal}
          footer={(
            <div className={styles.modalFooter}>
              <Button
                type='cancel'
                onClick={() => {
                  this.completeAudioTest(FAIL)
                  closeModal(AUDIO_ERROR_MODAL)
                }}
              >
                <FormattedMessage {...messages.skip} />
              </Button>
              <Button
                type='ok'
                onClick={() => {
                  closeModal(AUDIO_ERROR_MODAL)
                }}
              >
                <FormattedMessage {...messages.continue} />
              </Button>
            </div>
          )}
        >
          <ul>
            <li>
              <FormattedMessage {...messages.audioErrorModalTips} />
            </li>
            <li>
              <FormattedMessage {...messages.errorModalContinueOrSkip} />
            </li>
          </ul>
        </Modal>

        <Modal
          title={(
            <div className={styles.modalHeader}>
              <img src={FailBandwidth} alt="Bandwidth error icon" />
              <FormattedMessage {...messages.connectionErrorModalTitle} />
            </div>
          )}
          show={showSpeedErrorModal}
          footer={(
            <div className={styles.modalFooter}>
              <Button
                type='cancel'
                onClick={() => {
                  closeModal(SPEED_ERROR_MODAL)
                }}
              >
                <FormattedMessage {...messages.skip} />
              </Button>
              <Button
                type='ok'
                onClick={() => {
                  closeModal(SPEED_ERROR_MODAL)
                }}
              >
                <FormattedMessage {...messages.continue} />
              </Button>
            </div>
          )}
        >
          <ul>
            <li>
              <FormattedMessage {...messages.connectionErrorModalTips} />
            </li>
            <li>
              <FormattedMessage {...messages.errorModalContinueOrSkip} />
            </li>
          </ul>
        </Modal>

        <Container>
          <header>
            <Row>
              <Col sm={3}>
                { visitDetailsUrl && (
                  <a className='btn btn-success pull-left' data-testid='back-button' styleName='backToScheduling' href={visitDetailsUrl}>
                    <FormattedMessage {...messages.backToScheduling} />
                  </a>
                )}
              </Col>
            </Row>
            { showIntroduction && (
              <div data-testid='introduction-text' styleName='message'>
                <FormattedMessage {...messages.introduction} />
              </div>
            )}

            { showSuccessMessage && (
              <div data-testid='success-text' styleName='successMessage'>
                <FormattedMessage {...messages.successMessage} />
              </div>
            )}

            { showFailureMessage && (
              <div data-testid='error-text' styleName='failureMessage'>
                <FormattedMessage {...messages.failureMessage} />
              </div>
            )}
          </header>

          <Row>
            <Col sm={4} id='video-test' styleName={cn('testItem', {active: videoTestIsActive})} className='col-sm-4 video-test'>
              <i styleName='icon'>
                <img className='cameraIcon' src={cameraStatusIcon} alt="Camera status icon" />
              </i>
              <div data-testid='description' styleName='description'>
                <FormattedMessage {...messages.camera} />
              </div>
              <video autoPlay playsInline ref={this.videoEl} />
              { showVideoTestInstruction && (
                <div data-testid='instruction' styleName='instruction'>
                  <FormattedMessage {...messages.videoInstruction} />
                </div>
              )}
              { showVideoTestResult && (
                <div data-testid='result' styleName='result'>
                  <a onClick={() => openModal(CAMERA_ERROR_MODAL)} className='btn btn-default'>
                    <FormattedMessage {...messages.no} />
                  </a>
                  <a onClick={() => this.completeVideoTest(SUCCESS)} className='btn btn-primary'>
                    <FormattedMessage {...messages.yes} />
                  </a>
                </div>
              )}
            </Col>

            <Col sm={4} id='audio-test' styleName={cn('testItem', {active: audioTestIsActive, audioTest: audioAttached})} className='col-sm-4  audio-test'>
              <i styleName='icon'>
                <img className='microphone-icon' src={microphoneStatusIcon} alt="Microphone status icon" />
              </i>
              <div data-testid='description' styleName='description'>
                <FormattedMessage {...messages.microphone} />
              </div>
              <div data-testid='audio-instruction' styleName='instruction'>
                <FormattedMessage {...messages.audioInstruction} />
              </div>
              <div data-testid='audio-instruction' styleName='instruction'>
                <FormattedMessage {...messages.audioInstruction2} />
              </div>
              <div data-testid='volume' styleName='volume' className='progress'>
                <div styleName='volumeMeter' className='progress-bar' style={{ width: `${volume}%`}} />
              </div>
              <div id='audio-stream' />
              { showSpeakerTestInstruction && (
                <div data-testid='speaker-instruction' styleName='instruction'>
                  <FormattedMessage {...messages.speakerInstruction} />
                </div>
              )}
              { showSpeakerTestResult && (
                <div data-testid='result' styleName='result'>
                  <a onClick={() => openModal(AUDIO_ERROR_MODAL)} className='btn btn-default'>
                    <FormattedMessage {...messages.no} />
                  </a>
                  <a onClick={() => this.completeAudioTest(SUCCESS)} className='btn btn-primary'>
                    <FormattedMessage {...messages.yes} />
                  </a>
                </div>
              )}

            </Col>

            <Col sm={4} id='speed-test' styleName={cn('testItem', {active: speedTestIsActive})} className='col-sm-4 speed-test'>
              <i styleName='icon'>
                <img className='bandwidth-icon' src={bandwidthStatusIcon} alt="Bandwidth status icon" />
              </i>
              <div data-testid='description' styleName='description'>
                <FormattedMessage {...messages.speed} />
              </div>
              { showSpeedResult && (
                <div data-testid='result' styleName='result'>
                  <FormattedMessage {...messages.bandwidth} />
                  <div styleName='bandwidth'>{bandwidth}</div>
                  Mbps
                  <p>(<FormattedMessage {...messages.latency} />: {latency}ms)</p>
                </div>
              )}
            </Col>
          </Row>

          { showRunAgainMessage && (
            <Col sm={12} styleName='runAgain'>
              <a onClick={this.rerun}>
                <FormattedMessage {...messages.runAgain} />
              </a>
            </Col>
          )}

        </Container>
      </div>

    )
  }
}
export default EquipmentTests
